import React, { useState, useEffect } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import jsPDF from 'jspdf';

interface Resultado {
  dezena: string;
  data: string;
  hit: boolean;
  timestamp: number;
}

interface HistoricoPremios {
  [key: number]: Resultado[];
}

const DezenasPremiadas = () => {
  const [premioSelecionado, setPremioSelecionado] = useState(1);
  const [historicoPremios, setHistoricoPremios] = useState<HistoricoPremios>({
    1: [], 2: [], 3: [], 4: [], 5: []
  });
  const [dezenaResultado, setDezenaResultado] = useState("");
  const [dataResultado, setDataResultado] = useState("");
  const [valorInicial, setValorInicial] = useState(0.10);
  const [rodadasMax, setRodadasMax] = useState(5);
  const [showResults, setShowResults] = useState(false);
  const [anotacoes, setAnotacoes] = useState("");

  const dezenasPremidadas = [
    '86', '88', '94', '96', '90', '92', '98', '00', '34', '36',
    '66', '68', '10', '12', '02', '04', '38', '40', '78', '80',
    '54', '56', '42', '44', '22', '24', '06', '08', '82', '84'
  ];

  const bichosCorrespondentes = {
    '86-88': '22 - Tigre', '94-96': '24 - Veado', '90-92': '23 - Urso',
    '98-00': '25 - Vaca', '34-36': '09 - Cobra', '66-68': '17 - Macaco',
    '10-12': '03 - Burro', '02-04': '01 - Avestruz', '38-40': '10 - Coelho',
    '78-80': '20 - Peru', '54-56': '14 - Gato', '42-44': '11 - Cavalo',
    '22-24': '06 - Cabra', '06-08': '02 - Águia', '82-84': '21 - Touro'
  };

  useEffect(() => {
    setDataResultado(new Date().toISOString().split('T')[0]);
    carregarDados();
  }, []);

  const carregarDados = () => {
    const dadosSalvos = localStorage.getItem('dezenas_premiadas_dados');
    
    if (dadosSalvos) {
      const dados = JSON.parse(dadosSalvos);
      setHistoricoPremios(dados.historicoPremios || { 1: [], 2: [], 3: [], 4: [], 5: [] });
      
      if (dados.configuracoes) {
        setValorInicial(dados.configuracoes.valorInicial || 0.10);
        setRodadasMax(dados.configuracoes.rodadasMax || 5);
      }
      
      if (dados.anotacoes) {
        setAnotacoes(dados.anotacoes);
      }
    }

    const notasSalvas = localStorage.getItem('dezenas_premiadas_notas');
    if (notasSalvas && !anotacoes) {
      setAnotacoes(notasSalvas);
    }
  };

  const salvarDados = () => {
    const dados = {
      historicoPremios,
      configuracoes: {
        valorInicial,
        rodadasMax
      },
      anotacoes,
      timestamp: Date.now()
    };

    localStorage.setItem('dezenas_premiadas_dados', JSON.stringify(dados));
    localStorage.setItem('dezenas_premiadas_notas', anotacoes);
  };

  const adicionarResultado = () => {
    const dezena = dezenaResultado.padStart(2, '0');
    const data = dataResultado;
    
    if (!dezena || !data || dezena.length !== 2) {
      alert('⚠️ Por favor, preencha a dezena (00-99) e a data!');
      return;
    }

    const isHit = dezenasPremidadas.includes(dezena);
    const resultado: Resultado = {
      dezena: dezena,
      data: data,
      hit: isHit,
      timestamp: Date.now()
    };

    const novoHistorico = { ...historicoPremios };
    novoHistorico[premioSelecionado].unshift(resultado);
    
    // Manter apenas os últimos 15 resultados
    if (novoHistorico[premioSelecionado].length > 15) {
      novoHistorico[premioSelecionado] = novoHistorico[premioSelecionado].slice(0, 15);
    }

    setHistoricoPremios(novoHistorico);
    setDezenaResultado('');
    setDataResultado(new Date().toISOString().split('T')[0]);
    
    setTimeout(salvarDados, 100);
  };

  const verificarMomentoApostar = () => {
    const historico = historicoPremios[premioSelecionado];
    
    if (historico.length < 10) {
      return false;
    }

    const ultimos10 = historico.slice(0, 10);
    const temHit = ultimos10.some(resultado => resultado.hit);
    
    return !temHit;
  };

  const calcularProgessao = () => {
    setShowResults(true);
  };

  const limparDados = () => {
    if (confirm('⚠️ Tem certeza que deseja limpar todos os dados salvos?')) {
      setHistoricoPremios({ 1: [], 2: [], 3: [], 4: [], 5: [] });
      setAnotacoes('');
      setValorInicial(0.10);
      setRodadasMax(5);
      setDezenaResultado('');
      setShowResults(false);
      
      localStorage.removeItem('dezenas_premiadas_dados');
      localStorage.removeItem('dezenas_premiadas_notas');
      
      alert('🗑️ Todos os dados foram limpos!');
    }
  };

  const exportarDados = () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleString('pt-BR');
    
    // Header
    doc.setFontSize(20);
    doc.text('Dezenas Premiadas - Relatório', 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Data de Exportação: ${currentDate}`, 20, 35);
    doc.text(`Configurações - Valor inicial: R$ ${valorInicial.toFixed(2)}`, 20, 45);
    doc.text(`Rodadas máximas: ${rodadasMax}`, 20, 55);
    
    let yPos = 75;
    
    // Dezenas premiadas
    doc.setFontSize(14);
    doc.text('Dezenas Premiadas:', 20, yPos);
    yPos += 15;
    
    let xPos = 20;
    dezenasPremidadas.forEach((dezena, index) => {
      doc.setFontSize(10);
      doc.text(dezena, xPos, yPos);
      xPos += 20;
      
      if ((index + 1) % 8 === 0) {
        yPos += 10;
        xPos = 20;
      }
    });
    
    yPos += 30;
    
    // Histórico do prêmio selecionado
    if (historicoPremios[premioSelecionado].length > 0) {
      doc.setFontSize(14);
      doc.text(`Histórico do ${premioSelecionado}º Prêmio:`, 20, yPos);
      yPos += 15;
      
      historicoPremios[premioSelecionado].slice(0, 10).forEach((resultado, index) => {
        doc.setFontSize(10);
        doc.text(`${resultado.data} - ${resultado.dezena} ${resultado.hit ? '✓' : '✗'}`, 20, yPos);
        yPos += 10;
      });
      
      yPos += 20;
    }
    
    // Anotações
    if (anotacoes) {
      doc.setFontSize(14);
      doc.text('Anotações:', 20, yPos);
      
      const splitNotes = doc.splitTextToSize(anotacoes, 170);
      doc.setFontSize(10);
      doc.text(splitNotes, 20, yPos + 15);
    }
    
    doc.save(`dezenas_premiadas_${new Date().toISOString().split('T')[0]}.pdf`);
    alert('📤 PDF exportado com sucesso!');
  };

  // Generate progression table data
  const progressionData = [];
  let investimentoAcumulado = 0;
  for (let rodada = 1; rodada <= rodadasMax; rodada++) {
    const valorPorDezena = valorInicial * Math.pow(2, rodada - 1);
    const totalInvestido = valorPorDezena * 30; // 30 dezenas
    investimentoAcumulado += totalInvestido;
    const retornoSeGanhar = valorPorDezena * 80; // Pagamento 80 para 1
    const lucroLiquido = retornoSeGanhar - investimentoAcumulado;

    progressionData.push({
      rodada,
      valorPorDezena,
      totalInvestido,
      investimentoAcumulado,
      retorno: retornoSeGanhar,
      lucro: lucroLiquido
    });
  }

  const deveApostar = verificarMomentoApostar();
  const historico = historicoPremios[premioSelecionado];

  return (
    <div className="w-full max-w-full space-y-6 overflow-x-auto">
      <PageInstructions
        title="📖 Como usar as Dezenas Premiadas"
        steps={[
          "Veja o painel com todas as 25 dezenas e suas frequências",
          "As dezenas em destaque são as que mais saíram recentemente",
          "Selecione as dezenas que deseja apostar clicando nelas",
          "Configure a quantidade de jogos que deseja gerar",
          "Clique em 'Gerar Jogos' para criar combinações com as dezenas escolhidas",
          "Exporte em PDF para levar suas apostas prontas"
        ]}
      />
      {/* Header */}
      <div className="relative bg-gradient-to-br from-red-500 to-orange-600 text-white p-8 rounded-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-50 animate-pulse"></div>
        <div className="relative z-10 text-center space-y-4">
          <h1 className="text-4xl font-bold text-shadow-lg">🎯 Dezenas Premiadas</h1>
          <p className="text-xl opacity-95">Sistema avançado para maximizar ganhos no Jogo do Bicho</p>
          <div className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-6 py-3 rounded-full font-bold text-lg border-3 border-white/30">
            ⭐ AGORA PAGANDO 80 PARA 1! ⭐
          </div>
        </div>
      </div>

      {/* Strategy Overview */}
      <Card className="border-2 border-teal-300 bg-gradient-to-br from-teal-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="text-teal-600 text-2xl text-center">🧠 Como Funciona a Estratégia</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border-2 border-teal-400 rounded-xl p-6 text-center hover:transform hover:scale-105 transition-all duration-300 relative overflow-hidden group shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-teal-100/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">1</div>
                <h4 className="font-bold mb-2 text-gray-800">Acompanhe os Resultados</h4>
                <p className="text-sm text-gray-700 font-medium">Monitore os últimos 10 concursos e identifique quando nenhuma das 30 dezenas premiadas saiu em um prêmio específico.</p>
              </div>
            </div>
            <div className="bg-white border-2 border-teal-400 rounded-xl p-6 text-center hover:transform hover:scale-105 transition-all duration-300 relative overflow-hidden group shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-teal-100/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">2</div>
                <h4 className="font-bold mb-2 text-gray-800">Aguarde o Momento</h4>
                <p className="text-sm text-gray-700 font-medium">Após 10 concursos sem nenhuma das 30 dezenas no prêmio escolhido, é hora de apostar todas as 30 dezenas.</p>
              </div>
            </div>
            <div className="bg-white border-2 border-teal-400 rounded-xl p-6 text-center hover:transform hover:scale-105 transition-all duration-300 relative overflow-hidden group shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-teal-100/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">3</div>
                <h4 className="font-bold mb-2 text-gray-800">Aposte com Martingale</h4>
                <p className="text-sm text-gray-700 font-medium">Se não ganhar na primeira tentativa, dobre a aposta na próxima até acertar. Pagamento: 80x o valor apostado!</p>
              </div>
            </div>
            <div className="bg-white border-2 border-teal-400 rounded-xl p-6 text-center hover:transform hover:scale-105 transition-all duration-300 relative overflow-hidden group shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-teal-100/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">4</div>
                <h4 className="font-bold mb-2 text-gray-800">Lucro Garantido</h4>
                <p className="text-sm text-gray-700 font-medium">Com o alto índice de acerto e o sistema progressivo, o lucro é praticamente garantido a longo prazo.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dezenas Section */}
      <Card className="border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-yellow-50">
        <CardHeader>
          <CardTitle className="text-orange-600 text-2xl text-center">🎲 As 30 Dezenas Premiadas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 sm:gap-3 mb-6 p-3 sm:p-5 bg-white rounded-xl border-2 border-dashed border-orange-300">
            {dezenasPremidadas.map((dezena, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-3 rounded-xl text-center font-bold text-sm hover:from-red-500 hover:to-red-600 hover:scale-110 hover:rotate-6 transition-all duration-300 cursor-pointer relative overflow-hidden group shadow-lg"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-300"></div>
                <span className="relative z-10">{dezena}</span>
              </div>
            ))}
          </div>
            <div className="bg-white p-5 rounded-xl border-2 border-orange-400">
            <h4 className="text-orange-700 font-bold mb-3">📋 Correspondência com os 15 Bichos:</h4>
            <p className="text-gray-800 leading-relaxed font-medium">
              <strong>Pares de dezenas:</strong> Cada par corresponde a um bicho específico. 
              Esta seleção representa os bichos com maior probabilidade de saída baseado em análises estatísticas.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tracking Section */}
      <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-violet-50">
        <CardHeader>
          <CardTitle className="text-purple-600 text-2xl text-center">📊 Acompanhamento de Resultados</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-3 justify-center">
            {[1, 2, 3, 4, 5].map((premio) => (
              <Button
                key={premio}
                onClick={() => setPremioSelecionado(premio)}
                className={`${
                  premioSelecionado === premio 
                    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                    : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                } transition-all duration-300 hover:scale-105`}
              >
                {premio}º Prêmio
              </Button>
            ))}
          </div>

          <div className="bg-white border-2 border-purple-400 rounded-xl p-6">
            <h4 className="mb-4 text-purple-600 font-bold">Adicionar Resultado do {premioSelecionado}º Prêmio:</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <input
                type="text"
                placeholder="Dezena (00-99)"
                maxLength={2}
                value={dezenaResultado}
                onChange={(e) => {
                  const valor = e.target.value.replace(/\D/g, '');
                  if (valor.length <= 2) setDezenaResultado(valor);
                }}
                className="p-3 border-2 border-purple-400 rounded-lg text-center bg-white text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <input
                type="date"
                value={dataResultado}
                onChange={(e) => setDataResultado(e.target.value)}
                className="p-3 border-2 border-purple-400 rounded-lg text-center bg-white text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <Button
                onClick={adicionarResultado}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
              >
                ➕ Adicionar
              </Button>
            </div>
            
            {deveApostar && (
              <div className="bg-yellow-100 border-2 border-yellow-400 p-4 rounded-lg mb-4 animate-pulse">
                <h4 className="text-yellow-800 font-bold">🚨 MOMENTO DE APOSTAR!</h4>
                <p className="text-yellow-800">Já passaram 10+ concursos sem nenhuma das 30 dezenas no {premioSelecionado}º Prêmio!</p>
              </div>
            )}

            <div className="max-h-80 overflow-y-auto space-y-2">
              {historico.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nenhum resultado adicionado ainda.</p>
              ) : (
                historico.map((resultado, index) => (
                  <div key={index} className={`p-4 rounded-lg border-l-4 flex justify-between items-center ${
                    resultado.hit ? 'bg-green-50 border-l-green-500' : 'bg-gray-50 border-l-gray-400'
                  }`}>
                    <div>
                      <strong>Concurso {historico.length - index}</strong> - {new Date(resultado.data).toLocaleDateString('pt-BR')}
                    </div>
                    <div className="flex items-center gap-2">
                      Dezena: <strong>{resultado.dezena}</strong>
                      {resultado.hit ? (
                        <span className="text-green-600 font-bold">✅ HIT!</span>
                      ) : (
                        <span className="text-red-600">❌ Miss</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calculator Section */}
      <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="text-blue-600 text-2xl text-center">💰 Calculadora de Apostas Progressivas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="font-bold text-gray-800 text-lg">💵 Valor Inicial por Dezena:</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={valorInicial}
                onChange={(e) => setValorInicial(parseFloat(e.target.value) || 0.01)}
                className="w-full p-4 border-2 border-blue-400 rounded-lg text-center text-lg bg-white text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="space-y-2">
              <label className="font-bold text-gray-800 text-lg">🎯 Rodadas Máximas:</label>
              <input
                type="number"
                min="1"
                max="10"
                value={rodadasMax}
                onChange={(e) => setRodadasMax(parseInt(e.target.value) || 1)}
                className="w-full p-4 border-2 border-blue-400 rounded-lg text-center text-lg bg-white text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <Button
            onClick={calcularProgessao}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-4 text-lg rounded-full font-bold"
          >
            🔢 Calcular Progressão
          </Button>

          {showResults && (
            <div className="bg-white border-2 border-blue-400 rounded-lg p-6 animate-in slide-in-from-bottom-4 duration-500">
              <h4 className="text-blue-600 font-bold mb-5 text-center">📈 Tabela de Progressão:</h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                      <th className="p-3 text-center font-bold text-white">Rodada</th>
                      <th className="p-3 text-center font-bold text-white">Valor por Dezena</th>
                      <th className="p-3 text-center font-bold text-white">Total Investido</th>
                      <th className="p-3 text-center font-bold text-white">Investimento Acumulado</th>
                      <th className="p-3 text-center font-bold text-white">Retorno se Ganhar</th>
                      <th className="p-3 text-center font-bold text-white">Lucro Líquido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progressionData.map((row, index) => (
                      <tr key={index} className={`hover:bg-blue-50 ${row.lucro > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                        <td className="p-3 text-center font-bold text-gray-800">{row.rodada}ª</td>
                        <td className="p-3 text-center text-gray-800 font-semibold">R$ {row.valorPorDezena.toFixed(2)}</td>
                        <td className="p-3 text-center text-gray-800 font-semibold">R$ {row.totalInvestido.toFixed(2)}</td>
                        <td className="p-3 text-center text-gray-800 font-semibold">R$ {row.investimentoAcumulado.toFixed(2)}</td>
                        <td className="p-3 text-center text-gray-800 font-semibold">R$ {row.retorno.toFixed(2)}</td>
                        <td className={`p-3 text-center font-bold ${row.lucro > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          R$ {row.lucro.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Warning Section */}
      <Card className="border-2 border-red-300 bg-gradient-to-br from-red-50 to-pink-50">
        <CardHeader>
          <CardTitle className="text-red-600 text-xl text-center">⚠️ Regras Importantes da Estratégia</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 ml-4 text-slate-700 leading-relaxed text-lg">
            <li><strong>Jogue apenas em UM prêmio específico</strong> - não vale apostar nos 5 prêmios</li>
            <li><strong>Aguarde pelo menos 10 concursos</strong> sem hits antes de começar a apostar</li>
            <li><strong>Use o sistema progressivo</strong> - dobre a aposta se não ganhar</li>
            <li><strong>Tenha capital suficiente</strong> para pelo menos 3-4 progressões</li>
            <li><strong>Não desista nunca</strong> - continue até recuperar o investimento</li>
            <li><strong>Novo pagamento: 80 para 1</strong> - muito melhor que os 50 para 1 anteriores!</li>
          </ul>
        </CardContent>
      </Card>

      {/* Notes Section */}
      <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-violet-50">
        <CardHeader>
          <CardTitle className="text-purple-600 text-xl">📝 Suas Anotações e Observações:</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <textarea
            value={anotacoes}
            onChange={(e) => {
              setAnotacoes(e.target.value);
              localStorage.setItem('dezenas_premiadas_notas', e.target.value);
            }}
            placeholder="Anote aqui:
- Quais prêmios você está acompanhando
- Resultados observados
- Estratégias que funcionaram
- Controle de ganhos e perdas
- Observações sobre padrões..."
            className="w-full min-h-32 p-4 border-2 border-purple-400 rounded-lg text-sm resize-y bg-white text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Button onClick={salvarDados} className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700">
              💾 Salvar Dados
            </Button>
            <Button onClick={limparDados} variant="outline" className="border-gray-400 hover:bg-gray-100">
              🗑️ Limpar Dados
            </Button>
            <Button onClick={exportarDados} className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700">
              📤 Exportar PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DezenasPremiadas;